import { useCallback, useEffect, useRef, useState } from 'react'
import { buildOptimizationCandidates } from '../lib/optimization'
import type { OptimizationConfig, OptimizationResult } from '../lib/optimization'
import type { SimulationInputs } from '../types'
import type { OptimizationRequest, OptimizationResponse } from '../workers/optimization.worker'

/** Each batch owns a worker, so cancelling stops CPU work immediately. */
export function useOptimization(base: SimulationInputs) {
  const [result, setResult] = useState<OptimizationResult | null>(null)
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [completed, setCompleted] = useState(0)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState('')
  const workerRef = useRef<Worker | null>(null)
  const requestId = useRef(0)

  useEffect(() => () => {
    requestId.current += 1
    workerRef.current?.terminate()
    workerRef.current = null
  }, [])

  const cancel = useCallback(() => {
    requestId.current += 1
    workerRef.current?.terminate()
    workerRef.current = null
    setRunning(false)
    setProgress(0)
    setCompleted(0)
    setTotal(0)
    setError('')
  }, [])

  const run = useCallback((config: OptimizationConfig) => {
    if (workerRef.current) return
    setError('')
    try {
      const candidates = buildOptimizationCandidates(base, config)
      const id = ++requestId.current
      const worker = new Worker(new URL('../workers/optimization.worker.ts', import.meta.url), { type: 'module' })
      workerRef.current = worker
      setRunning(true)
      setProgress(0)
      setCompleted(0)
      setTotal(candidates.length)

      const isCurrent = () => requestId.current === id && workerRef.current === worker
      const finish = () => {
        worker.terminate()
        workerRef.current = null
        setRunning(false)
      }
      worker.onmessage = ({ data }: MessageEvent<OptimizationResponse>) => {
        if (!isCurrent() || data.id !== id) return
        if (data.type === 'progress') {
          setProgress(data.progress)
          setCompleted(data.completed)
          setTotal(data.total)
        } else if (data.type === 'result') {
          setResult(data.result)
          setProgress(100)
          setCompleted(data.result.rows.length)
          setTotal(data.result.rows.length)
          finish()
        } else {
          setError(data.message)
          finish()
        }
      }
      worker.onerror = (event) => {
        if (!isCurrent()) return
        setError(event.message || '优化计算线程异常，请重试。')
        finish()
      }
      worker.onmessageerror = () => {
        if (!isCurrent()) return
        setError('无法读取优化结果，请重试。')
        finish()
      }
      worker.postMessage({ id, base, config } satisfies OptimizationRequest)
    } catch (cause) {
      workerRef.current?.terminate()
      workerRef.current = null
      setRunning(false)
      setError(cause instanceof Error ? cause.message : '无法启动参数优化。')
    }
  }, [base])

  return { result, running, progress, completed, total, error, run, cancel }
}
